import React from 'react'
import { Box, Button, Divider, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import InsightsIcon from '@mui/icons-material/Insights'
import ActionButton from '../system/ActionButton'

// The Tables page header's buttons and the unsaved-changes banner. Drawn
// here rather than inline so the page-tool coverage test can render them
// (#1534).

export interface TablesHeaderActionsProps {
  /** A table (or an AutoCoder table) is open. */
  hasTable: boolean
  isAutoCoder: boolean
  isPreviewMode: boolean
  onEdit: () => void
  onNewTable: () => void
  /** Opens and closes Insights (the page helper). Open at every AI level: the pane says what needs more. */
  onInsights: () => void
}

export const TablesHeaderActions: React.FC<TablesHeaderActionsProps> = ({ hasTable, isAutoCoder, isPreviewMode, onEdit, onNewTable, onInsights }) => {
  if (!hasTable) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <ActionButton icon={<AddIcon />} label="New Table" onClick={onNewTable} />
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <ActionButton icon={<EditIcon />} label="Edit Spec" onClick={onEdit} />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
      {!isAutoCoder && (
        <>
          <ActionButton icon={<AddIcon />} label="New Table" onClick={onNewTable} />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '6px' }} />
        </>
      )}
      {!isPreviewMode && (
        // data-insights-toggle: a click here is the button's own, not a click
        // away from the pane (paneClickAway), so it closes the pane once.
        <Box component="span" data-insights-toggle sx={{ display: 'inline-flex' }}>
          <ActionButton icon={<InsightsIcon />} label="Insights" onClick={onInsights} />
        </Box>
      )}
    </Box>
  )
}

export interface TablesPreviewBannerProps {
  isPreviewMode: boolean
  saving: boolean
  onDiscard: () => void
  onSaveSettings: () => void
  onSaveTable: () => void
}

export const TablesPreviewBanner: React.FC<TablesPreviewBannerProps> = ({ isPreviewMode, saving, onDiscard, onSaveSettings, onSaveTable }) => (
  <Box sx={{
    m: 2,
    px: 2.5,
    py: 1.5,
    borderRadius: 1.5,
    backgroundColor: 'info.main',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
  }}>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {isPreviewMode ? 'Preview mode — changes are not saved yet' : 'View settings have changed'}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
      <Button
        variant="text"
        size="small"
        onClick={onDiscard}
        disabled={saving}
        sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
      >
        Discard
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onSaveSettings}
        disabled={saving}
        sx={{
          color: 'white',
          borderColor: 'rgba(255,255,255,0.5)',
          '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
        }}
      >
        Save Settings
      </Button>
      <Button
        variant="contained"
        size="small"
        onClick={onSaveTable}
        disabled={saving}
        sx={{
          backgroundColor: 'white',
          color: 'info.dark',
          fontWeight: 600,
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
        }}
      >
        Save Table
      </Button>
    </Box>
  </Box>
)
